import * as XLSX from 'xlsx';

export function readWorkbookFromArrayBuffer(arrayBuffer) {
  return XLSX.read(arrayBuffer, { type: 'array' });
}

export function getWorkbookSheetNames(workbook) {
  return workbook?.SheetNames || [];
}

export function sheetToRowObjects(workbook, sheetName) {
  const sheet = workbook?.Sheets?.[sheetName];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json(sheet, { defval: '' });
}

export function isWorkbookTotalsRow(row) {
  if (!row || typeof row !== 'object') return true;
  const values = Object.values(row).map((value) => String(value || '').trim());
  const nonEmpty = values.filter(Boolean);
  if (nonEmpty.length === 0) return true;
  if (nonEmpty.length === 1 && /^count\s*:/i.test(nonEmpty[0])) return true;
  const first = nonEmpty[0] || '';
  if (/^(count|total|totals)\s*:/i.test(first)) return true;
  return false;
}

export function filterWorkbookRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => !isWorkbookTotalsRow(row));
}

function getRawNamePart(camper, part) {
  const rawRow = camper?.rawRow && typeof camper.rawRow === 'object' ? camper.rawRow : {};
  const partPattern = part === 'first' ? /first.*name|name.*first/i : /last.*name|name.*last/i;
  const key = Object.keys(rawRow).find((candidate) => partPattern.test(candidate));
  return key && rawRow[key] ? String(rawRow[key]).trim() : '';
}

export function splitCamperName(camper = {}) {
  const explicitFirst = String(camper.firstName || getRawNamePart(camper, 'first') || '').trim();
  const explicitLast = String(camper.lastName || getRawNamePart(camper, 'last') || '').trim();
  if (explicitFirst || explicitLast) {
    return { firstName: explicitFirst, lastName: explicitLast };
  }
  const fullName = String(camper.name || '').trim().replace(/\s+/g, ' ');
  if (!fullName) return { firstName: '', lastName: '' };
  if (fullName.includes(',')) {
    const [lastName, ...firstParts] = fullName.split(',');
    return { firstName: firstParts.join(',').trim(), lastName: lastName.trim() };
  }
  const [firstName, ...lastParts] = fullName.split(' ');
  return { firstName, lastName: lastParts.join(' ') };
}

export function formatSkillForExport(skill) {
  const normalized = String(skill || '').trim();
  return normalized === 'ONE EIGHT' ? '1:8' : normalized;
}

export function buildScheduleExportFilename(locationName, year, label, extension) {
  const safeLocation = String(locationName || 'Camp')
    .replace(/[\\/?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'Camp';
  const safeYear = Number.isFinite(Number(year)) ? String(Number(year)) : String(new Date().getFullYear());
  return `${safeLocation} ${safeYear} - ${label}.${extension}`;
}

function getCamperAssignmentKey(camper) {
  return camper?.camperId || camper?.name || '';
}

function getCamperGradeForExport(camper) {
  const rawRow = camper?.rawRow && typeof camper.rawRow === 'object' ? camper.rawRow : {};
  const gradeKey = Object.keys(rawRow).find((key) => key.toLowerCase().includes('grade'));
  if (gradeKey && rawRow[gradeKey] !== '' && rawRow[gradeKey] != null) {
    return rawRow[gradeKey];
  }
  return camper?.grade === 0 ? 'K' : camper?.grade ?? '';
}

export function buildCamperScheduleRows(campers, assignments, groups) {
  return (campers || []).map((camper) => {
    const { firstName, lastName } = splitCamperName(camper);
    const camperAssignments = assignments?.[getCamperAssignmentKey(camper)] || {};
    const skills = Array.from({ length: 6 }, (_, rotation) => {
      const groupKey = camperAssignments[rotation];
      return formatSkillForExport(groupKey ? groups?.[rotation]?.[groupKey]?.skill : '');
    });
    return [
      firstName,
      lastName,
      String(camper.tribe || '').toUpperCase(),
      camper.gender || '',
      ...skills,
    ];
  });
}

function titleCaseSkill(skill) {
  return formatSkillForExport(skill)
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase())
    .replace(/[^A-Za-z0-9 _-]/g, '')
    .trim()
    .slice(0, 31) || 'Skill';
}

function getUniqueSkillSheetNames(skills) {
  const used = new Set(['MASTER']);
  const names = {};
  skills.forEach((skill) => {
    const base = titleCaseSkill(skill);
    let name = base;
    let suffix = 2;
    while (used.has(name)) {
      const suffixText = ` ${suffix}`;
      name = `${base.slice(0, 31 - suffixText.length)}${suffixText}`;
      suffix += 1;
    }
    used.add(name);
    names[skill] = name;
  });
  return names;
}

function applyReferenceSheetLayout(sheet, columnCount, rowCount, blockStarts = []) {
  sheet['!cols'] = Array.from({ length: columnCount }, (_, index) => ({
    wch: blockStarts.some((start) => index === start) ? 22
      : blockStarts.some((start) => index === start + 1) ? 18
        : blockStarts.some((start) => index === start + 2) ? 20
          : blockStarts.some((start) => index === start + 3) ? 18
            : 3,
  }));
  sheet['!rows'] = Array.from({ length: Math.max(2, rowCount) }, (_, index) => ({ hpt: index === 0 ? 22 : 18 }));
}

function applyHeaderStyle(sheet, address, fillColor = 'B7B7B7') {
  if (!sheet[address]) return;
  sheet[address].s = {
    fill: { patternType: 'solid', fgColor: { rgb: fillColor } },
    font: { bold: true, color: { rgb: '000000' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };
}

export function buildSkillRosterWorkbook(campers, assignments, groups) {
  const workbook = XLSX.utils.book_new();
  const masterHeader = [
    'First Name',
    'Last Name',
    'CAMPER Tribe',
    'CAMPER Gender',
    'Skill 1',
    'Skill 2',
    'Skill 3',
    'Skill 4',
    'Skill 5',
    'Skill 6',
  ];
  const masterRows = buildCamperScheduleRows(campers, assignments, groups)
    .slice()
    .sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
  const masterSheet = XLSX.utils.aoa_to_sheet([masterHeader, ...masterRows]);
  masterSheet['!cols'] = [
    { wch: 16 }, { wch: 20 }, { wch: 14 }, { wch: 15 },
    { wch: 19 }, { wch: 19 }, { wch: 19 }, { wch: 19 }, { wch: 19 }, { wch: 19 },
  ];
  masterSheet['!rows'] = [{ hpt: 22 }, ...masterRows.map(() => ({ hpt: 18 }))];
  masterSheet['!autofilter'] = { ref: `A1:J${masterRows.length + 1}` };
  for (let column = 0; column < masterHeader.length; column += 1) {
    applyHeaderStyle(masterSheet, XLSX.utils.encode_cell({ r: 0, c: column }), 'D9D9D9');
  }
  XLSX.utils.book_append_sheet(workbook, masterSheet, 'MASTER');

  const skills = Array.from(new Set((groups || []).flatMap((rotationGroups) => (
    Object.values(rotationGroups || {})
      .filter((group) => group && group.enabled !== false && (group.campers || []).length > 0)
      .map((group) => group.skill)
  )))).sort((a, b) => formatSkillForExport(a).localeCompare(formatSkillForExport(b)));
  const sheetNames = getUniqueSkillSheetNames(skills);
  const blockStarts = [0, 5, 10, 15, 20, 25];

  skills.forEach((skill) => {
    const rosters = Array.from({ length: 6 }, (_, rotation) => {
      const byCamper = new Map();
      Object.values(groups?.[rotation] || {}).forEach((group) => {
        if (!group || group.enabled === false || group.skill !== skill) return;
        (group.campers || []).forEach((camper) => {
          byCamper.set(getCamperAssignmentKey(camper), camper);
        });
      });
      return Array.from(byCamper.values()).sort((a, b) => (
        String(a.name || '').localeCompare(String(b.name || ''))
      ));
    });
    const maxRosterSize = Math.max(0, ...rosters.map((roster) => roster.length));
    const rowCount = maxRosterSize + 2;
    const columnCount = 29;
    const rows = Array.from({ length: rowCount }, () => Array(columnCount).fill(''));
    const merges = [];
    blockStarts.forEach((start, rotation) => {
      rows[0][start] = `SKILL ${rotation + 1}`;
      rows[1][start] = 'Name';
      rows[1][start + 1] = 'Participant: Grade';
      rows[1][start + 2] = 'Participant: Gender';
      rows[1][start + 3] = `Rotation ${rotation + 1}`;
      merges.push({ s: { r: 0, c: start }, e: { r: 0, c: start + 3 } });
      rosters[rotation].forEach((camper, rosterIndex) => {
        const rowIndex = rosterIndex + 2;
        rows[rowIndex][start] = camper.name || '';
        rows[rowIndex][start + 1] = getCamperGradeForExport(camper);
        rows[rowIndex][start + 2] = camper.gender || '';
        rows[rowIndex][start + 3] = formatSkillForExport(skill);
      });
    });
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet['!merges'] = merges;
    applyReferenceSheetLayout(sheet, columnCount, rowCount, blockStarts);
    blockStarts.forEach((start) => {
      applyHeaderStyle(sheet, XLSX.utils.encode_cell({ r: 0, c: start }));
    });
    XLSX.utils.book_append_sheet(workbook, sheet, sheetNames[skill]);
  });
  return workbook;
}

export function buildRotationTotalsRows(groups) {
  const rows = [];
  (groups || []).forEach((rotationGroups, rotationIndex) => {
    Object.keys(rotationGroups || {})
      .sort()
      .forEach((groupKey) => {
        const group = rotationGroups[groupKey];
        if (!group || group.enabled === false || (group.campers || []).length === 0) return;
        rows.push({
          Rotation: rotationIndex + 1,
          Group: groupKey,
          Skill: group.skill,
          Campers: (group.campers || []).length,
          Staff: group.staff || '',
        });
      });
  });
  return rows;
}

export function buildStaffWorkbook(staffAssignments, groups) {
  const workbook = XLSX.utils.book_new();
  const staffRows = [];
  Object.keys(staffAssignments || {}).forEach((staffName) => {
    const row = { 'Staff Name': staffName };
    const assignments = staffAssignments[staffName] || [];
    for (let r = 0; r < assignments.length; r += 1) {
      const groupKey = assignments[r];
      if (!groupKey) {
        row[`Rotation ${r + 1} Skill`] = '';
        row[`Rotation ${r + 1} Roster`] = '';
        continue;
      }
      const group = groups?.[r]?.[groupKey];
      row[`Rotation ${r + 1} Skill`] = group ? `${group.skill} (${groupKey})` : '';
      row[`Rotation ${r + 1} Roster`] = group ? (group.campers || []).map((camper) => camper.name).join('; ') : '';
    }
    staffRows.push(row);
  });
  const totalsRows = buildRotationTotalsRows(groups);
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(staffRows), 'Staff');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(totalsRows), 'Rotation Totals');
  return workbook;
}

export function writeWorkbookArrayBuffer(workbook) {
  return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
}
